import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Target, Users, Lightbulb, ShieldCheck, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sermon AI Scaffolding — Logos-parity Phase 3. Four quick prompts the
 * pastor can fire while drafting:
 *   • Big Idea       — distill outline into one memorable sentence
 *   • Audience       — 3 audience profiles + tailoring notes
 *   • Illustrations  — 3 concrete illustrations for this passage
 *   • Critique       — strengths / risks / improvement from a wise mentor
 *
 * All grounded in the current draft (topic + scripture + outline).
 */
const SermonAiScaffold = ({ topic, scripture, outline }) => {
  const { language } = useLanguage();
  const isES = language === 'es';
  const [loadingType, setLoadingType] = useState(null);
  const [results, setResults] = useState({}); // {prompt_type: data}

  const PROMPTS = [
    { type: 'big_idea',      icon: Target,      labelEn: 'Big Idea',      labelEs: 'Idea central' },
    { type: 'audience',      icon: Users,       labelEn: 'Audience',      labelEs: 'Audiencia' },
    { type: 'illustrations', icon: Lightbulb,   labelEn: 'Illustrations', labelEs: 'Ilustraciones' },
    { type: 'critique',      icon: ShieldCheck, labelEn: 'Critique',      labelEs: 'Crítica' },
  ];

  const handleRun = async (promptType) => {
    if (loadingType) return;
    setLoadingType(promptType);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/ai/sermon-scaffold`,
        {
          prompt_type: promptType,
          topic: topic || '',
          scripture: scripture || '',
          outline: (outline || []).map((p) => `${p.title || ''}: ${(p.content || '').slice(0, 200)}`),
        },
        { withCredentials: true },
      );
      setResults((prev) => ({ ...prev, [promptType]: r.data?.result }));
    } catch (e) {
      console.error('Sermon AI scaffold failed:', e);
      toast.error(isES ? 'IA no disponible' : 'AI unavailable');
    } finally {
      setLoadingType(null);
    }
  };

  const renderResult = (promptType, data) => {
    if (!data) return null;
    if (promptType === 'big_idea') {
      return <p className="font-semibold text-base text-slate-800 dark:text-slate-100 italic">"{data.big_idea}"</p>;
    }
    if (promptType === 'audience') {
      return (
        <ul className="space-y-2">
          {(data.profiles || []).map((p, i) => (
            <li key={i} className="text-sm">
              <span className="font-bold text-purple-700 dark:text-purple-300">{p.audience}</span>
              <span className="text-slate-600 dark:text-slate-400"> — {p.note}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (promptType === 'illustrations') {
      return (
        <ul className="space-y-3">
          {(data.illustrations || []).map((ill, i) => (
            <li key={i} className="text-sm">
              <div className="font-bold text-amber-700 dark:text-amber-300">{ill.title}</div>
              <p className="text-slate-700 dark:text-slate-300">{ill.body}</p>
            </li>
          ))}
        </ul>
      );
    }
    if (promptType === 'critique') {
      return (
        <div className="space-y-2 text-sm">
          {data.strength && (
            <p><span className="font-bold text-green-700 dark:text-green-300">{isES ? 'Fortaleza:' : 'Strength:'}</span> {data.strength}</p>
          )}
          {Array.isArray(data.risks) && data.risks.length > 0 && (
            <div>
              <span className="font-bold text-red-700 dark:text-red-300">{isES ? 'Riesgos:' : 'Risks:'}</span>
              <ul className="list-disc list-inside ml-2 mt-0.5">
                {data.risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {data.improvement && (
            <p><span className="font-bold text-blue-700 dark:text-blue-300">{isES ? 'Mejora:' : 'Improvement:'}</span> {data.improvement}</p>
          )}
        </div>
      );
    }
    return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
  };

  return (
    <Card className="border-l-4 border-purple-500 bg-purple-50/30 dark:bg-purple-950/15" data-testid="sermon-ai-scaffold">
      <CardHeader className="pb-2">
        <h3 className="text-sm font-bold text-purple-800 dark:text-purple-200 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          {isES ? 'Coach IA' : 'AI Sermon Coach'}
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-400">
          {isES
            ? 'Cuatro preguntas para afinar tu sermón mientras lo escribes.'
            : 'Four prompts to sharpen your sermon while you draft.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PROMPTS.map((p) => (
            <Button
              key={p.type}
              onClick={() => handleRun(p.type)}
              disabled={loadingType === p.type}
              variant="outline"
              size="sm"
              data-testid={`scaffold-${p.type}`}
              className="border-purple-300 text-purple-800 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900/40"
            >
              {loadingType === p.type ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <p.icon className="w-3.5 h-3.5 mr-1.5" />}
              {isES ? p.labelEs : p.labelEn}
            </Button>
          ))}
        </div>

        {Object.entries(results).map(([type, data]) => (
          <Card key={type} className="bg-white dark:bg-slate-900 shadow-sm" data-testid={`scaffold-result-${type}`}>
            <CardContent className="p-3 relative">
              <button
                onClick={() => setResults((p) => { const c = { ...p }; delete c[type]; return c; })}
                className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p className="text-[10px] uppercase tracking-widest text-purple-600 dark:text-purple-400 mb-2">
                {PROMPTS.find((x) => x.type === type)?.[isES ? 'labelEs' : 'labelEn']}
              </p>
              {renderResult(type, data)}
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
};

export default SermonAiScaffold;
